import React, { FC, useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useLocation,
} from "react-router-dom";
import { connect, ConnectedProps } from "react-redux";
import {
  ThemeProvider,
  Grid,
  LinearProgress,
  styled,
  Button,
} from "@mui/material";
import { withAITracking } from "@microsoft/applicationinsights-react-js";
import { reactPlugin } from "./telemetry/appInsights";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "./authConfig";

import IFrame from "./components/IFrame/IFrame";
import UserPage from "./components/UserPage/UserPage";
import IOPDrawer from "./components/IOPDrawer/IOPDrawer";
import PowerBI from "./components/PowerBI/PowerBI";
import ContentPage from "./components/ContentPage/ContentPage";
import theme from "./theme";
import { fetchUserAction } from "./redux/actions";

// Redux connection
const mapState = (state: State) => ({
  user: state.user,
});
const mapDispatchToProps = { fetchUser: fetchUserAction };
const connector = connect(mapState, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;
type Props = PropsFromRedux;

// Styled components
const RootContainer = styled("div")({
  display: "flex",
  minHeight: "100vh",
});

const ContentContainer = styled("main")({
  backgroundColor: "#f4f4f4",
  display: "flex",
  flexGrow: 1,
});

const UpdateTitle: FC = () => {
  const location = useLocation();
  useEffect(() => {
    const pathParts = location.pathname.split("/");
    const routeName = pathParts[pathParts.length - 1] || "Home";
    document.title = `OI - ${routeName.charAt(0).toUpperCase() + routeName.slice(1)}`;
  }, [location]);
  return null;
};

// Protected route wrapper
const ProtectedRoute: FC<{ children: React.ReactNode }> = ({ children }) => {
  const { accounts } = useMsal();
  return accounts.length > 0 ? <>{children}</> : <Navigate to="/" />;
};

const App: FC<Props> = ({ user, fetchUser }) => {
  const [hasReloaded, setHasReloaded] = useState<boolean>(false);
  const { instance, accounts } = useMsal();
  const isAuthenticated = accounts.length > 0;

  // Set active account
  useEffect(() => {
    if (isAuthenticated) {
      instance.setActiveAccount(accounts[0]);
    }
  }, [isAuthenticated, accounts, instance]);

  // Simulate reload delay
  useEffect(() => {
    const timer = setTimeout(() => setHasReloaded(true), 2000);
    return () => clearTimeout(timer);
  }, []);

  // Secure token acquisition before fetching user
  useEffect(() => {
    const fetchUserSecurely = async () => {
      try {
        const response = await instance.acquireTokenSilent({
          ...loginRequest,
          account: accounts[0],
        });
        if (response.accessToken) {
          fetchUser(); // Optionally pass token to action
        }
      } catch (error) {
        console.error("Token acquisition failed", error);
        instance.loginPopup(loginRequest); // fallback
      }
    };

    if (isAuthenticated) {
      fetchUserSecurely();
    }
  }, [isAuthenticated, fetchUser, instance, accounts]);

  const handleLogin = () => {
    instance.loginPopup(loginRequest).catch((e) => console.error(e));
  };

  return (
    <Router>
      <ThemeProvider theme={theme}>
        {!isAuthenticated ? (
          <Grid container justifyContent="center" alignItems="center" style={{ minHeight: "100vh" }}>
            <Grid item xs={6}>
              <Button variant="contained" onClick={handleLogin}>
                Login with Microsoft
              </Button>
            </Grid>
          </Grid>
        ) : user && hasReloaded ? (
          <RootContainer style={{ overflow: "hidden" }}>
            <IOPDrawer />
            <ContentContainer>
              <UpdateTitle />
              <Routes>
                <Route path="/" element={<ProtectedRoute><ContentPage /></ProtectedRoute>} />
                <Route path="/powerbi/:path" element={<ProtectedRoute><PowerBI type="powerbi" /></ProtectedRoute>} />
                <Route path="/embed/:path" element={<ProtectedRoute><IFrame type="embed" /></ProtectedRoute>} />
                <Route path="/instructions/:path" element={<ProtectedRoute><IFrame type="instructions" /></ProtectedRoute>} />
                <Route path="/page/:path" element={<ProtectedRoute><ContentPage /></ProtectedRoute>} />
                <Route path="/user" element={<ProtectedRoute><UserPage /></ProtectedRoute>} />
              </Routes>
            </ContentContainer>
          </RootContainer>
        ) : (
          <Grid container justifyContent="center" alignItems="center" style={{ minHeight: "100vh" }}>
            <Grid item xs={6}>
              <LinearProgress color="secondary" />
            </Grid>
          </Grid>
        )}
      </ThemeProvider>
    </Router>
  );
};

export default withAITracking(reactPlugin, connector(App));